import React, { useEffect } from 'react';
import type { ThemeColors } from '../types';

/**
 * Applies a workspace's AI-generated brand color palette to the document root
 * as CSS custom properties. When colors are null/undefined, restores the
 * default Conistra logo-aligned palette.
 */

const DEFAULT_COLORS: ThemeColors = {
  primary_50: '#fff7ed',
  primary_100: '#ffedd5',
  primary_200: '#fed7aa',
  primary_500: '#fb923c',
  primary_600: '#f97316',
  primary_700: '#ea580c',
  primary_900: '#9a3412',
  accent_500: '#ff4f98',
  accent_600: '#e53882',
};

function hexToRgb(hex: string, separator = ' '): string {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r}${separator}${g}${separator}${b}`;
}

function applyTheme(colors: ThemeColors, animate = false, isDefault = false): void {
  const root = document.documentElement;

  if (animate) {
    root.classList.add('theme-transitioning');
    // Remove the class after the transition completes
    setTimeout(() => root.classList.remove('theme-transitioning'), 900);
  }

  root.style.setProperty('--color-primary-50', colors.primary_50);
  root.style.setProperty('--color-primary-100', colors.primary_100);
  root.style.setProperty('--color-primary-200', colors.primary_200);
  root.style.setProperty('--color-primary-400', colors.primary_500);
  root.style.setProperty('--color-primary-500', colors.primary_500);
  root.style.setProperty('--color-primary-600', colors.primary_600);
  root.style.setProperty('--color-primary-700', colors.primary_700);
  root.style.setProperty('--color-primary-900', colors.primary_900);
  root.style.setProperty('--color-accent-500', colors.accent_500);
  root.style.setProperty('--color-accent-600', colors.accent_600);

  // Also update derived RGB values used for rgba() constructs
  root.style.setProperty('--color-primary-500-rgb', hexToRgb(colors.primary_500, ' '));
  root.style.setProperty('--color-primary-600-rgb', hexToRgb(colors.primary_600, ' '));
  root.style.setProperty('--color-primary-700-rgb', hexToRgb(colors.primary_700, ' '));
  root.style.setProperty('--color-accent-500-rgb', hexToRgb(colors.accent_500, ' '));

  // Update chat bubble user color
  root.style.setProperty('--chat-bubble-user', colors.primary_600);
  root.style.setProperty('--chat-muted', `rgba(${hexToRgb(colors.primary_600, ', ')}, 0.12)`);
  root.style.setProperty('--chat-highlight', `rgba(${hexToRgb(colors.accent_500, ', ')}, 0.18)`);

  // The one signal gradient. For the default brand we keep the canonical
  // 3-stop gradient declared in index.css (pink → coral → orange). For a
  // custom workspace palette we re-derive a 2-stop accent → primary gradient
  // so per-workspace theming still reaches every gradient surface.
  if (isDefault) {
    root.style.removeProperty('--gradient-signal');
  } else {
    root.style.setProperty(
      '--gradient-signal',
      `linear-gradient(150deg, ${colors.accent_500} 0%, ${colors.primary_600} 100%)`
    );
  }
}

// Track the last applied colors to detect real changes

export function useTheme(colors: ThemeColors | null | undefined): void {
  const lastApplied = React.useRef<string | null>(null);

  useEffect(() => {
    const isDefault = colors == null;
    const target = colors ?? DEFAULT_COLORS;
    const key = JSON.stringify(target);
    const shouldAnimate = lastApplied.current !== null && lastApplied.current !== key;
    lastApplied.current = key;
    applyTheme(target, shouldAnimate, isDefault);
  }, [colors]);
}

/**
 * Applies theme colors directly (outside of React — e.g. during activation reveal).
 * Returns a cleanup function that restores the default palette.
 */
export function applyThemeColors(colors: ThemeColors): () => void {
  applyTheme(colors, true, false);
  return () => applyTheme(DEFAULT_COLORS, true, true);
}
